import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksApi, usersApi, propertiesApi, unavailabilitiesApi } from '../services/api';
import Sidebar from '../components/Sidebar';
import PropertyHistorySearch from '../components/PropertyHistorySearch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  CalendarDays, 
  Plus, 
  Search,
  MapPin,
  Users,
  Euro,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  UserPlus,
  UserMinus,
  CalendarOff,
  X,
  MessageSquare,
  Key,
  Building2,
  BedDouble,
  Trash2,
  CalendarClock,
  Settings,
  Clock,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  format, 
  addDays, 
  addMonths,
  addYears,
  subMonths,
  subYears,
  startOfWeek, 
  endOfWeek, 
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  eachDayOfInterval, 
  eachMonthOfInterval,
  isSameDay, 
  isSameMonth,
  getDay,
  parseISO 
} from 'date-fns';
import { fr } from 'date-fns/locale';

const Planning = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [housekeepers, setHousekeepers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [dialogUnavailabilities, setDialogUnavailabilities] = useState([]); // For dialog date selection
  const [loading, setLoading] = useState(true);
  
  // Initialiser la date depuis l'URL, localStorage ou aujourd'hui
  const getInitialDate = () => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsed = parseISO(dateParam);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    // Fallback: localStorage
    const savedDate = localStorage.getItem('planning_selected_date');
    if (savedDate) {
      const parsed = parseISO(savedDate);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  };
  const [selectedDate, setSelectedDate] = useState(() => getInitialDate());
  
  // Sauvegarder la date sélectionnée dans localStorage à chaque changement
  useEffect(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    localStorage.setItem('planning_selected_date', dateStr);
  }, [selectedDate]);

  // Initialiser le viewMode depuis localStorage
  const getInitialViewMode = () => {
    const saved = localStorage.getItem('planning_view_mode');
    return saved || 'day';
  };
  const [viewMode, setViewMode] = useState(() => getInitialViewMode());

  // Memoize expensive month/year totals used in stats panels
  const monthTasks = useMemo(
    () => tasks.filter(t => isSameMonth(parseISO(t.date), selectedDate)),
    [tasks, selectedDate]
  );
  const monthCA = useMemo(
    () => monthTasks.reduce((sum, t) => sum + (t.price || 0), 0),
    [monthTasks]
  );
  const yearTasks = useMemo(
    () => tasks.filter(t => parseISO(t.date).getFullYear() === selectedDate.getFullYear()),
    [tasks, selectedDate]
  );
  const yearCA = useMemo(
    () => yearTasks.reduce((sum, t) => sum + (t.price || 0), 0),
    [yearTasks]
  );
  
  // Sauvegarder le viewMode
  useEffect(() => {
    localStorage.setItem('planning_view_mode', viewMode);
  }, [viewMode]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUnavailDialog, setShowUnavailDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [assignData, setAssignData] = useState({ housekeeper_ids: [], price: '', notes: '', temporary_note: '', is_urgent: false });
  const [createData, setCreateData] = useState({ 
    property_id: '', 
    date: format(new Date(), 'yyyy-MM-dd'), 
    guests_count: '', 
    price: '',
    can_start_early: false,
    guest_departure_time: '',
    owner_arrival: '',
    housekeeper_ids: [],
    is_not_billable: false  // Pour dépôt de linge, etc.
  });
  const [unavailData, setUnavailData] = useState({ user_id: '', date: '', reason: '', period: 'full' });
  const [rescheduleData, setRescheduleData] = useState({ new_date: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  // Filtre FDM : "all" ou l'ID d'une FDM. Persisté en localStorage.
  const [filterHousekeeper, setFilterHousekeeper] = useState(() => {
    try { return localStorage.getItem('planning_filter_hk') || 'all'; } catch { return 'all'; }
  });
  useEffect(() => {
    try { localStorage.setItem('planning_filter_hk', filterHousekeeper); } catch (_e) { /* ignore */ }
  }, [filterHousekeeper]);
  const [propertySearch, setPropertySearch] = useState('');
  const [propertySearchOpen, setPropertySearchOpen] = useState(false);
  const [showTaskOptionsDialog, setShowTaskOptionsDialog] = useState(false);
  const [taskOptionsData, setTaskOptionsData] = useState({
    can_start_early: false,
    guest_departure_time: '',
    owner_arrival: '',
    is_urgent: false,
    linen_included: false,
    temporary_note: ''
  });
  const [selectedTaskForOptions, setSelectedTaskForOptions] = useState(null);

  // Filter properties based on search
  const filteredProperties = properties.filter(p => {
    if (!propertySearch) return true;
    const search = propertySearch.toLowerCase();
    return (
      p.name?.toLowerCase().includes(search) ||
      p.address?.toLowerCase().includes(search) ||
      p.city?.toLowerCase().includes(search)
    );
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      let dateFrom, dateTo;
      
      if (viewMode === 'day') {
        params.date = format(selectedDate, 'yyyy-MM-dd');
        dateFrom = dateTo = format(selectedDate, 'yyyy-MM-dd');
      } else {
        const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
        params.date_from = format(start, 'yyyy-MM-dd');
        params.date_to = format(end, 'yyyy-MM-dd');
        dateFrom = params.date_from;
        dateTo = params.date_to;
      }

      const [tasksRes, hkRes, propsRes, unavailRes] = await Promise.all([
        tasksApi.list(params),
        usersApi.listAssignable(),
        propertiesApi.list(),
        unavailabilitiesApi.getByRange(dateFrom, dateTo)
      ]);
      setTasks(tasksRes.data);
      setHousekeepers(hkRes.data);
      setProperties(propsRes.data);
      setUnavailabilities(unavailRes.data);
    } catch (error) {
      console.error('Planning error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unavailabilities for a specific date
  const getUnavailabilitiesForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return unavailabilities.filter(u => u.date === dateStr);
  };

  // Label for a unavailability period (admin views)
  const getPeriodLabel = (period) => {
    switch ((period || 'full').toLowerCase()) {
      case 'morning': return 'Matin';
      case 'afternoon': return 'Après-midi';
      default: return 'Journée';
    }
  };

  // Check if a specific housekeeper is unavailable on a date
  const isHousekeeperUnavailable = (housekeeperId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Check both main unavailabilities and dialog-specific ones
    const allUnavail = [...unavailabilities, ...dialogUnavailabilities];
    return allUnavail.find(u => u.user_id === housekeeperId && u.date === dateStr);
  };

  // Fetch unavailabilities for a specific date (used in dialogs)
  const fetchUnavailabilitiesForDate = async (dateStr) => {
    try {
      const res = await unavailabilitiesApi.getByRange(dateStr, dateStr);
      setDialogUnavailabilities(res.data || []);
    } catch (error) {
      console.error('Error fetching unavailabilities:', error);
    }
  };

  // Fetch custom prices for selected housekeepers (dialog assignation)
  const fetchCustomPrices = async (propertyId, userIds) => {
    if (!propertyId || userIds.length === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/properties/${propertyId}/price-for-users?user_ids=${userIds.join(',')}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setAssignData(prev => ({
          ...prev,
          price: data.total.toString(),
          priceDetails: data.prices
        }));
      }
    } catch (error) {
      console.error('Error fetching custom prices:', error);
    }
  };

  // Fetch custom prices for create dialog
  const fetchCustomPricesForCreate = async (propertyId, userIds) => {
    if (!propertyId || userIds.length === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/properties/${propertyId}/price-for-users?user_ids=${userIds.join(',')}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setCreateData(prev => ({
          ...prev,
          price: data.total.toString()
        }));
      }
    } catch (error) {
      console.error('Error fetching custom prices for create:', error);
    }
  };

  const handleAddUnavailability = async () => {
    if (!unavailData.user_id || !unavailData.date) {
      toast.error('Sélectionnez un employé et une date');
      return;
    }
    setActionLoading(true);
    try {
      await unavailabilitiesApi.create(unavailData);
      toast.success('Indisponibilité enregistrée');
      setShowUnavailDialog(false);
      setUnavailData({ user_id: '', date: '', reason: '', period: 'full' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveUnavailability = async (unavailId) => {
    try {
      await unavailabilitiesApi.delete(unavailId);
      toast.success('Indisponibilité supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleData.new_date) {
      toast.error('Sélectionnez une nouvelle date');
      return;
    }
    setActionLoading(true);
    try {
      await tasksApi.reschedule(selectedTask.id, { new_date: rescheduleData.new_date });
      toast.success(`Date modifiée au ${format(new Date(rescheduleData.new_date), 'd MMMM yyyy', { locale: fr })}`);
      setShowRescheduleDialog(false);
      setRescheduleData({ new_date: '' });
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de date');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTask = async () => {
    setActionLoading(true);
    try {
      await tasksApi.delete(selectedTask.id);
      toast.success('Prestation supprimée');
      setShowDeleteDialog(false);
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async () => {
    if (assignData.housekeeper_ids.length === 0) {
      toast.error('Sélectionnez au moins une femme de ménage');
      return;
    }
    if (assignData.housekeeper_ids.length > 2) {
      toast.error('Maximum 2 femmes de ménage par tâche');
      return;
    }
    setActionLoading(true);
    try {
      await tasksApi.assign(selectedTask.id, {
        housekeeper_ids: assignData.housekeeper_ids,
        price: assignData.price ? parseFloat(assignData.price) : undefined,
        notes: assignData.notes || undefined,
        is_urgent: assignData.is_urgent
      });
      toast.success('Tâche assignée');
      setShowAssignDialog(false);
      setAssignData({ housekeeper_ids: [], price: '', notes: '', is_urgent: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createData.property_id || !createData.date) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    setActionLoading(true);
    try {
      // Si non facturable, forcer le prix à 0
      const finalPrice = createData.is_not_billable ? 0 : (createData.price ? parseFloat(createData.price) : undefined);
      
      await tasksApi.create({
        property_id: createData.property_id,
        date: createData.date,
        guests_count: createData.guests_count ? parseInt(createData.guests_count) : undefined,
        price: finalPrice,
        can_start_early: createData.can_start_early,
        guest_departure_time: createData.guest_departure_time || undefined,
        owner_arrival: createData.owner_arrival || undefined,
        housekeeper_ids: createData.housekeeper_ids.length > 0 ? createData.housekeeper_ids : undefined,
        is_not_billable: createData.is_not_billable || undefined
      });
      toast.success('Tâche créée');
      setShowCreateDialog(false);
      setCreateData({ 
        property_id: '', 
        date: format(selectedDate, 'yyyy-MM-dd'), 
        guests_count: '', 
        price: '',
        can_start_early: false,
        guest_departure_time: '',
        owner_arrival: '',
        housekeeper_ids: [],
        is_not_billable: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const openTaskOptions = (task) => {
    setSelectedTaskForOptions(task);
    setTaskOptionsData({
      can_start_early: task.can_start_early || false,
      guest_departure_time: task.guest_departure_time || '',
      owner_arrival: task.owner_arrival || '',
      is_urgent: task.is_urgent || false,
      linen_included: task.linen_included || task.property_linen_included || false,
      temporary_note: task.temporary_note || ''
    });
    setShowTaskOptionsDialog(true);
  };

  const handleSaveTaskOptions = async () => {
    if (!selectedTaskForOptions) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tasks/${selectedTaskForOptions.id}/options`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(taskOptionsData)
      });
      if (res.ok) {
        toast.success('Options mises à jour');
        setShowTaskOptionsDialog(false);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      en_attente: 'bg-orange-100 text-orange-700',
      affectee: 'bg-blue-100 text-blue-700',
      acceptee: 'bg-sky-100 text-sky-700',
      en_cours: 'bg-purple-100 text-purple-700',
      termine: 'bg-emerald-100 text-emerald-700',
      probleme: 'bg-red-100 text-red-700',
      non_realisable: 'bg-red-200 text-red-800 font-semibold',
      rejete: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      en_attente: 'En attente',
      affectee: 'Affectée',
      acceptee: 'Acceptée',
      en_cours: 'En cours',
      termine: 'Terminé',
      probleme: 'Problème',
      non_realisable: '🚫 Non réalisable',
      rejete: 'Rejeté'
    };
    return (
      <Badge className={`${styles[status] || 'bg-gray-100 text-gray-700'} border-0`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const filteredTasks = tasks.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterHousekeeper !== 'all') {
      // Une tâche match si la FDM sélectionnée est dans housekeeper_ids OU housekeeper_id
      const ids = Array.isArray(t.housekeeper_ids) && t.housekeeper_ids.length > 0
        ? t.housekeeper_ids
        : (t.housekeeper_id ? [t.housekeeper_id] : []);
      if (!ids.includes(filterHousekeeper)) return false;
    }
    return true;
  });

  const weekDays = viewMode === 'week' 
    ? eachDayOfInterval({
        start: startOfWeek(selectedDate, { weekStartsOn: 1 }),
        end: endOfWeek(selectedDate, { weekStartsOn: 1 })
      })
    : [];

  const getTasksForDay = (date) => {
    return filteredTasks.filter(t => t.date === format(date, 'yyyy-MM-dd'));
  };

  const TaskCard = ({ task }) => {
    const displayNames = task.housekeeper_names && task.housekeeper_names.length > 0 
      ? task.housekeeper_names.join(' & ') 
      : task.housekeeper_name;
    
    const handleRescheduleClick = (e) => {
      e.stopPropagation();
      setSelectedTask(task);
      setRescheduleData({ new_date: format(addDays(new Date(task.date), 1), 'yyyy-MM-dd') });
      setShowRescheduleDialog(true);
    };

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      setSelectedTask(task);
      setShowDeleteDialog(true);
    };
    
    return (
      <div 
        className={`p-3 rounded-lg border bg-white mb-2 cursor-pointer card-hover ${task.is_urgent ? 'border-l-4 border-l-red-500' : 'border-gray-100'}`}
        onClick={() => {
          if (task.status === 'en_attente') {
            setSelectedTask(task);
            // Ne PAS pré-remplir le prix - il sera calculé automatiquement selon la FDM sélectionnée
            setAssignData({ ...assignData, housekeeper_ids: [], price: '', priceDetails: null });
            // Fetch unavailabilities for the task date
            if (task.date) {
              fetchUnavailabilitiesForDate(task.date);
            }
            setShowAssignDialog(true);
          } else {
            navigate(`/admin/tasks/${task.id}`);
          }
        }}
        data-testid={`task-${task.id}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">{task.property_name}</span>
              {task.is_urgent && <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{task.property_address}</span>
            </div>
            {/* Access codes and linen info */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {task.property_keybox_code && (
                <div className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200" title="Code boîte à clés">
                  <Key className="h-3 w-3" />
                  <span>{task.property_keybox_code}</span>
                </div>
              )}
              {task.property_building_code && (
                <div className="flex items-center gap-1 text-xs bg-slate-50 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200" title="Code immeuble">
                  <Building2 className="h-3 w-3" />
                  <span>{task.property_building_code}</span>
                </div>
              )}
              <div 
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${(task.linen_included ?? task.property_linen_included) ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                title={(task.linen_included ?? task.property_linen_included) ? 'Linge de lit inclus' : 'Linge de lit NON inclus'}
              >
                <BedDouble className="h-3 w-3" />
                <span>{(task.linen_included ?? task.property_linen_included) ? 'Linge inclus' : 'Sans linge'}</span>
              </div>
              {/* Options de la prestation */}
              {task.can_start_early && (
                <div className="flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full border border-green-200" title="Peut être fait tôt">
                  <CheckCircle2 className="h-3 w-3" />
                  <span>Tôt OK</span>
                </div>
              )}
              {task.guest_departure_time && (
                <div className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200" title="Heure de départ">
                  <Clock className="h-3 w-3" />
                  <span>{task.guest_departure_time}</span>
                </div>
              )}
            </div>
            {/* Arrivée propriétaire bien visible */}
            {task.owner_arrival && (
              <div className="mt-2 p-2 bg-amber-100 border-2 border-amber-400 rounded-lg">
                <span className="text-sm font-bold text-amber-800">⚠️ {task.owner_arrival}</span>
              </div>
            )}
            {/* Note temporaire */}
            {task.temporary_note && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-sm text-blue-700">📝 {task.temporary_note}</span>
              </div>
            )}
            {displayNames && (
              <div className="flex items-center gap-1 text-sm text-[#003366] mt-2">
                <Users className="h-3 w-3" />
                <span>{displayNames}</span>
              </div>
            )}
          </div>
          <div className="ml-2 flex flex-col items-end gap-2">
            {getStatusBadge(task.status)}
            <span className="text-sm font-medium text-gray-700">{task.price || 0}€</span>
            {/* Temps passé si terminé */}
            {task.status === 'termine' && task.time_spent_minutes && (
              <div className="flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                <Clock className="h-3 w-3" />
                <span>
                  {Math.floor(task.time_spent_minutes / 60) > 0 && `${Math.floor(task.time_spent_minutes / 60)}h`}
                  {task.time_spent_minutes % 60 > 0 && `${task.time_spent_minutes % 60}min`}
                </span>
              </div>
            )}
            {/* Admin action buttons */}
            <div className="flex items-center gap-1 mt-1">
              <button
                onClick={(e) => { e.stopPropagation(); openTaskOptions(task); }}
                className="p-1.5 rounded bg-amber-50 hover:bg-amber-100 text-amber-600 border border-amber-200 transition-colors"
                title="Options de la prestation"
                data-testid={`options-task-${task.id}`}
              >
                <Settings className="h-4 w-4" />
              </button>
              <button
                onClick={handleRescheduleClick}
                className="p-1.5 rounded bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200 transition-colors"
                title="Changer la date"
                data-testid={`reschedule-task-${task.id}`}
              >
                <CalendarClock className="h-4 w-4" />
              </button>
              <button
                onClick={handleDeleteClick}
                className="p-1.5 rounded bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 transition-colors"
                title="Supprimer la prestation"
                data-testid={`delete-task-${task.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto ml-0 md:ml-72">
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Planning</h1>
              {/* Mobile refresh button */}
              <button 
                onClick={() => {
                  if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.getRegistrations().then(registrations => {
                      registrations.forEach(registration => registration.update());
                    });
                    caches.keys().then(names => {
                      names.forEach(name => caches.delete(name));
                    });
                  }
                  window.location.reload(true);
                }}
                className="md:hidden p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200"
                title="Actualiser l'application"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  setUnavailData({ ...unavailData, date: format(selectedDate, 'yyyy-MM-dd') });
                  setShowUnavailDialog(true);
                }}
                className="text-red-600 border-red-200 hover:bg-red-50 rounded-xl h-10"
                data-testid="add-off-btn"
              >
                <CalendarOff className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Ajouter OFF</span>
              </Button>
              <Button 
                onClick={() => {
                  const dateStr = format(selectedDate, 'yyyy-MM-dd');
                  setCreateData(prev => ({ ...prev, date: dateStr }));
                  fetchUnavailabilitiesForDate(dateStr);
                  setShowCreateDialog(true);
                }}
                className="bg-blue-900 hover:bg-blue-800 rounded-xl h-10"
                data-testid="create-task-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nouvelle tâche</span>
              </Button>
            </div>
          </div>

          {/* Controls - Mobile optimized */}
          <div className="flex flex-col gap-4 mb-6">
            {/* View mode tabs - Full width on mobile */}
            <div className="flex bg-slate-100 rounded-2xl p-1">
              <button
                onClick={() => setViewMode('day')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  viewMode === 'day' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="view-day"
              >
                Jour
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  viewMode === 'week' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="view-week"
              >
                Semaine
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  viewMode === 'month' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="view-month"
              >
                Mois
              </button>
              <button
                onClick={() => setViewMode('year')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  viewMode === 'year' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="view-year"
              >
                Année
              </button>
            </div>

            {/* Date picker row */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-10 w-10 rounded-xl" 
                onClick={() => {
                  if (viewMode === 'day') setSelectedDate(prev => addDays(prev, -1));
                  else if (viewMode === 'week') setSelectedDate(prev => addDays(prev, -7));
                  else if (viewMode === 'month') setSelectedDate(prev => addMonths(prev, -1));
                  else if (viewMode === 'year') setSelectedDate(prev => addYears(prev, -1));
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 h-10 rounded-xl text-sm font-medium" data-testid="date-picker">
                    <CalendarDays className="h-4 w-4 mr-2" />
                    {viewMode === 'day' && format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                    {viewMode === 'week' && `Semaine du ${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'd MMMM', { locale: fr })}`}
                    {viewMode === 'month' && format(selectedDate, 'MMMM yyyy', { locale: fr })}
                    {viewMode === 'year' && format(selectedDate, 'yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
              
              <Button 
                variant="outline" 
                size="icon" 
                className="h-10 w-10 rounded-xl" 
                onClick={() => {
                  if (viewMode === 'day') setSelectedDate(prev => addDays(prev, 1));
                  else if (viewMode === 'week') setSelectedDate(prev => addDays(prev, 7));
                  else if (viewMode === 'month') setSelectedDate(prev => addMonths(prev, 1));
                  else if (viewMode === 'year') setSelectedDate(prev => addYears(prev, 1));
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              <Button 
                variant="outline" 
                className="h-10 rounded-xl text-sm font-medium" 
                onClick={() => setSelectedDate(new Date())}
              >
                Aujourd'hui
              </Button>
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap items-center gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[180px] h-10 rounded-xl" data-testid="filter-status">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filtrer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="affectee">Affectée</SelectItem>
                  <SelectItem value="acceptee">Acceptée</SelectItem>
                  <SelectItem value="en_cours">En cours</SelectItem>
                  <SelectItem value="termine">Terminé</SelectItem>
                  <SelectItem value="probleme">Problème</SelectItem>
                </SelectContent>
              </Select>

              {/* Filtre par FDM — visible uniquement pour Admin/Tech (housekeepers.length > 0) */}
              {housekeepers.length > 0 && (
                <Select value={filterHousekeeper} onValueChange={setFilterHousekeeper}>
                  <SelectTrigger
                    className={`w-full sm:w-[220px] h-10 rounded-xl ${filterHousekeeper !== 'all' ? 'border-[#003366] ring-1 ring-[#003366]/40 bg-blue-50' : ''}`}
                    data-testid="filter-housekeeper"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filtrer par FDM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les FDM</SelectItem>
                    {housekeepers.map(hk => (
                      <SelectItem key={hk.id} value={hk.id}>
                        {hk.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {filterHousekeeper !== 'all' && (
                <button
                  type="button"
                  onClick={() => setFilterHousekeeper('all')}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-blue-100 text-blue-800 text-xs font-medium hover:bg-blue-200 transition"
                  data-testid="clear-fdm-filter"
                  title="Retirer le filtre FDM"
                >
                  {housekeepers.find(h => h.id === filterHousekeeper)?.name || 'FDM'} ×
                </button>
              )}

              <Button variant="outline" className="h-10 rounded-xl sm:hidden" onClick={() => setSelectedDate(new Date())}>
                Auj.
              </Button>
            </div>

            {/* Recherche logement avec historique */}
            <PropertyHistorySearch 
              onNavigateToTask={(task) => {
                // Naviguer vers la date de la tâche et ouvrir le détail
                setSelectedDate(new Date(task.date));
                navigate(`/admin/tasks/${task.id}`);
              }}
            />
          </div>

          {/* Unavailabilities Banner for Day View */}
          {viewMode === 'day' && getUnavailabilitiesForDate(selectedDate).length > 0 && (
            <Card className="border-0 shadow-sm bg-red-50 mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarOff className="h-5 w-5 text-red-600" />
                  <span className="font-semibold text-red-800">Congés ce jour</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getUnavailabilitiesForDate(selectedDate).map(u => {
                    const period = (u.period || 'full').toLowerCase();
                    const isHalf = period === 'morning' || period === 'afternoon';
                    return (
                      <Badge 
                        key={u.id} 
                        className={`border-0 flex items-center gap-1 pr-1 ${
                          isHalf ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mr-1 ${
                          isHalf ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'
                        }`}>
                          {getPeriodLabel(period).toUpperCase()}
                        </span>
                        {u.user_name} {u.reason && `(${u.reason})`}
                        <button 
                          onClick={() => handleRemoveUnavailability(u.id)}
                          className={`ml-1 rounded-full p-0.5 ${
                            isHalf ? 'hover:bg-orange-200' : 'hover:bg-red-200'
                          }`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : viewMode === 'day' ? (
            <div className="space-y-4">
              {filteredTasks.length === 0 ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-8 text-center">
                    <CalendarDays className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">Aucune tâche pour cette date</p>
                  </CardContent>
                </Card>
              ) : (
                filteredTasks.map(task => <TaskCard key={task.id} task={task} />)
              )}
            </div>
          ) : viewMode === 'week' ? (
            /* Week View - Modern Design */
            <div className="space-y-4">
              {/* Week Navigation Bar */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {weekDays.map((day) => {
                  const isToday = isSameDay(day, new Date());
                  const dayTasks = getTasksForDay(day);
                  const completedCount = dayTasks.filter(t => t.status === 'termine').length;
                  
                  return (
                    <button
                      key={format(day, 'yyyy-MM-dd')}
                      onClick={() => setSelectedDate(day)}
                      className={`flex-shrink-0 w-20 p-3 rounded-2xl text-center transition-all duration-300 ${
                        isToday 
                          ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30 scale-105' 
                          : isSameDay(day, selectedDate)
                            ? 'bg-white shadow-md border-2 border-blue-200'
                            : 'bg-white/60 hover:bg-white hover:shadow-md'
                      }`}
                    >
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${isToday ? 'text-blue-100' : 'text-slate-400'}`}>
                        {format(day, 'EEE', { locale: fr })}
                      </p>
                      <p className={`text-2xl font-bold my-1 ${isToday ? 'text-white' : 'text-slate-800'}`}>
                        {format(day, 'd')}
                      </p>
                      {dayTasks.length > 0 && (
                        <div className="flex items-center justify-center gap-1">
                          <span className={`text-xs font-bold ${isToday ? 'text-white' : 'text-blue-600'}`}>
                            {dayTasks.length}
                          </span>
                          {completedCount > 0 && (
                            <div className="flex items-center">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Tasks Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {weekDays.map((day) => {
                  const dayTasks = getTasksForDay(day);
                  const dayUnavailabilities = getUnavailabilitiesForDate(day);
                  const isToday = isSameDay(day, new Date());
                  
                  if (dayTasks.length === 0 && dayUnavailabilities.length === 0) return null;
                  
                  return (
                    <div key={format(day, 'yyyy-MM-dd')} className="space-y-3">
                      {/* Day Header */}
                      <div className={`flex items-center gap-3 px-1 ${isToday ? 'text-blue-600' : 'text-slate-600'}`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg ${
                          isToday ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {format(day, 'd')}
                        </div>
                        <div>
                          <p className="font-semibold capitalize">{format(day, 'EEEE', { locale: fr })}</p>
                          <p className="text-xs text-slate-400">{format(day, 'd MMMM', { locale: fr })}</p>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                          <span className="text-xs bg-slate-100 px-2 py-1 rounded-full font-medium">
                            {dayTasks.length} tâche{dayTasks.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {/* Unavailabilities */}
                      {dayUnavailabilities.map(u => {
                        const period = (u.period || 'full').toLowerCase();
                        const isHalf = period === 'morning' || period === 'afternoon';
                        return (
                          <div 
                            key={u.id}
                            className={`flex items-center gap-3 p-4 rounded-2xl border ${
                              isHalf
                                ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200'
                                : 'bg-gradient-to-r from-rose-50 to-orange-50 border-rose-100'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              isHalf ? 'bg-orange-100' : 'bg-rose-100'
                            }`}>
                              <CalendarOff className={`h-5 w-5 ${isHalf ? 'text-orange-600' : 'text-rose-500'}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className={`font-semibold ${isHalf ? 'text-orange-700' : 'text-rose-700'}`}>
                                  Indisponible
                                </p>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded text-white ${
                                  isHalf ? 'bg-orange-500' : 'bg-rose-500'
                                }`}>
                                  {getPeriodLabel(period).toUpperCase()}
                                </span>
                              </div>
                              <p className={`text-sm ${isHalf ? 'text-orange-500' : 'text-rose-500'}`}>
                                {u.user_name}{u.reason ? ` · ${u.reason}` : ''}
                              </p>
                            </div>
                            <button 
                              onClick={() => handleRemoveUnavailability(u.id)}
                              className={`p-2 rounded-xl transition-colors ${
                                isHalf ? 'hover:bg-orange-100' : 'hover:bg-rose-100'
                              }`}
                            >
                              <X className={`h-4 w-4 ${isHalf ? 'text-orange-400' : 'text-rose-400'}`} />
                            </button>
                          </div>
                        );
                      })}

                      {/* Task Cards */}
                      {dayTasks.map(task => {
                        const getStatusStyle = () => {
                          if (task.status === 'termine') return 'from-emerald-500 to-teal-500';
                          if (task.status === 'en_cours') return 'from-violet-500 to-purple-500';
                          if (task.is_urgent) return 'from-red-500 to-rose-500';
                          if (task.status === 'en_attente') return 'from-amber-400 to-orange-400';
                          return 'from-blue-500 to-indigo-500';
                        };

                        const getStatusLabel = () => {
                          if (task.status === 'termine') return { text: 'Terminé', icon: '✓' };
                          if (task.status === 'en_cours') return { text: 'En cours', icon: '▶' };
                          if (task.status === 'en_attente') return { text: 'À assigner', icon: '?' };
                          if (task.status === 'acceptee') return { text: 'Acceptée', icon: '✓' };
                          return { text: 'Affectée', icon: '→' };
                        };

                        const status = getStatusLabel();

                        return (
                          <div
                            key={task.id}
                            onClick={() => {
                              if (task.status === 'en_attente') {
                                setSelectedTask(task);
                                // Ne PAS pré-remplir le prix - il sera calculé automatiquement selon la FDM sélectionnée
                                setAssignData({ ...assignData, housekeeper_ids: [], price: '', priceDetails: null });
                                // Fetch unavailabilities for the task date
                                if (task.date) {
                                  fetchUnavailabilitiesForDate(task.date);
                                }
                                setShowAssignDialog(true);
                              } else {
                                navigate(`/admin/tasks/${task.id}`);
                              }
                            }}
                            className="group relative bg-white rounded-2xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-slate-100 hover:border-slate-200 hover:-translate-y-1"
                          >
                            {/* Color indicator bar */}
                            <div 
                              className="absolute left-0 top-4 bottom-4 w-1 rounded-full"
                              style={{ backgroundColor: task.property_color || '#3B82F6' }}
                            />

                            <div className="pl-4">
                              {/* Header */}
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                                    {task.property_name}
                                  </h4>
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    {task.guest_departure_time || 'Horaire flexible'}
                                  </p>
                                </div>
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold text-white bg-gradient-to-r ${getStatusStyle()}`}>
                                  {status.icon} {status.text}
                                </span>
                              </div>

                              {/* Assignee */}
                              {task.housekeeper_name && (
                                <div className="flex items-center gap-2 mb-3">
                                  <div 
                                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                                    style={{ backgroundColor: task.property_color || '#3B82F6' }}
                                  >
                                    {task.housekeeper_name.charAt(0)}
                                  </div>
                                  <span className="text-sm text-slate-600">{task.housekeeper_name}</span>
                                </div>
                              )}

                              {/* Tags */}
                              <div className="flex flex-wrap gap-1.5">
                                {task.can_start_early && (
                                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-md text-[10px] font-medium">
                                    ✓ Tôt OK
                                  </span>
                                )}
                                {task.owner_arrival && (
                                  <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded-md text-[10px] font-medium">
                                    ⚠ {task.owner_arrival}
                                  </span>
                                )}
                                {!task.property_linen_included && (
                                  <span className="px-2 py-0.5 bg-slate-50 text-slate-500 rounded-md text-[10px] font-medium">
                                    Sans linge
                                  </span>
                                )}
                                {task.property_keybox_code && (
                                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md text-[10px] font-medium">
                                    🔑 {task.property_keybox_code}
                                  </span>
                                )}
                              </div>

                              {/* Price */}
                              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                <span className="text-lg font-bold text-slate-800">{task.price || 0}€</span>
                                <span className="text-xs text-slate-400 group-hover:text-blue-500 transition-colors">
                                  Voir détails →
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Empty State */}
              {weekDays.every(day => getTasksForDay(day).length === 0 && getUnavailabilitiesForDate(day).length === 0) && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center">
                    <CalendarDays className="h-10 w-10 text-slate-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-600 mb-2">Aucune tâche cette semaine</h3>
                  <p className="text-slate-400 text-sm">Créez une nouvelle tâche pour commencer</p>
                </div>
              )}
            </div>
          ) : viewMode === 'month' ? (
            <div className="space-y-4">
              {/* Month Calendar Grid */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                {/* Days of week header */}
                <div className="grid grid-cols-7 border-b border-slate-100">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                    <div key={day} className="py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar days */}
                <div className="grid grid-cols-7">
                  {(() => {
                    const monthStart = startOfMonth(selectedDate);
                    const monthEnd = endOfMonth(selectedDate);
                    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
                    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
                    const days = eachDayOfInterval({ start: startDate, end: endDate });
                    
                    return days.map((day) => {
                      const isCurrentMonth = isSameMonth(day, selectedDate);
                      const isToday = isSameDay(day, new Date());
                      const dayTasks = tasks.filter(t => t.date === format(day, 'yyyy-MM-dd'));
                      const completedCount = dayTasks.filter(t => t.status === 'termine').length;
                      const pendingCount = dayTasks.length - completedCount;
                      
                      return (
                        <div
                          key={format(day, 'yyyy-MM-dd')}
                          onClick={() => {
                            setSelectedDate(day);
                            setViewMode('day');
                          }}
                          className={`min-h-[100px] p-2 border-b border-r border-slate-100 cursor-pointer transition-all hover:bg-blue-50 ${
                            !isCurrentMonth ? 'bg-slate-50/50' : ''
                          } ${isToday ? 'bg-blue-50' : ''}`}
                        >
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-semibold text-sm mb-1 ${
                            isToday 
                              ? 'bg-blue-600 text-white' 
                              : isCurrentMonth 
                                ? 'text-slate-700' 
                                : 'text-slate-300'
                          }`}>
                            {format(day, 'd')}
                          </div>
                          
                          {dayTasks.length > 0 && (
                            <div className="space-y-1">
                              {dayTasks.slice(0, 3).map(task => (
                                <div 
                                  key={task.id}
                                  className="text-[10px] px-1.5 py-0.5 rounded-md truncate text-white font-medium"
                                  style={{ backgroundColor: task.property_color || '#3B82F6' }}
                                >
                                  {task.property_name}
                                </div>
                              ))}
                              {dayTasks.length > 3 && (
                                <div className="text-[10px] text-slate-400 font-medium pl-1">
                                  +{dayTasks.length - 3} autres
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Month Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-4 border border-slate-100">
                  <p className="text-slate-500 text-sm mb-1">Total tâches</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {tasks.filter(t => {
                      const taskDate = parseISO(t.date);
                      return isSameMonth(taskDate, selectedDate);
                    }).length}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-slate-100">
                  <p className="text-slate-500 text-sm mb-1">Terminées</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    {tasks.filter(t => {
                      const taskDate = parseISO(t.date);
                      return isSameMonth(taskDate, selectedDate) && t.status === 'termine';
                    }).length}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-slate-100">
                  <p className="text-slate-500 text-sm mb-1">En cours</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {tasks.filter(t => {
                      const taskDate = parseISO(t.date);
                      return isSameMonth(taskDate, selectedDate) && !['termine', 'en_attente'].includes(t.status);
                    }).length}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-slate-100">
                  <p className="text-slate-500 text-sm mb-1">CA estimé</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {monthCA}€
                  </p>
                </div>
              </div>
            </div>
          ) : viewMode === 'year' ? (
            <div className="space-y-4">
              {/* Year Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {eachMonthOfInterval({
                  start: startOfYear(selectedDate),
                  end: endOfYear(selectedDate)
                }).map((month) => {
                  const monthTasks = tasks.filter(t => {
                    const taskDate = parseISO(t.date);
                    return isSameMonth(taskDate, month);
                  });
                  const completedCount = monthTasks.filter(t => t.status === 'termine').length;
                  const totalRevenue = monthTasks.reduce((sum, t) => sum + (t.price || 0), 0);
                  const isCurrentMonth = isSameMonth(month, new Date());
                  
                  return (
                    <div
                      key={format(month, 'yyyy-MM')}
                      onClick={() => {
                        setSelectedDate(month);
                        setViewMode('month');
                      }}
                      className={`bg-white rounded-2xl p-4 border cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 ${
                        isCurrentMonth ? 'border-blue-300 ring-2 ring-blue-100' : 'border-slate-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className={`font-bold capitalize ${isCurrentMonth ? 'text-blue-600' : 'text-slate-800'}`}>
                          {format(month, 'MMMM', { locale: fr })}
                        </h3>
                        {isCurrentMonth && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-bold">
                            Actuel
                          </span>
                        )}
                      </div>
                      
                      {/* Mini stats */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Tâches</span>
                          <span className="font-semibold text-slate-700">{monthTasks.length}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Terminées</span>
                          <span className="font-semibold text-emerald-600">{completedCount}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">CA</span>
                          <span className="font-bold text-blue-600">{totalRevenue}€</span>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      {monthTasks.length > 0 && (
                        <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all"
                            style={{ width: `${(completedCount / monthTasks.length) * 100}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Year Summary */}
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-4 text-blue-100">
                  Résumé {format(selectedDate, 'yyyy')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-blue-200 text-sm mb-1">Total tâches</p>
                    <p className="text-3xl font-bold">
                      {tasks.filter(t => {
                        const taskDate = parseISO(t.date);
                        return taskDate.getFullYear() === selectedDate.getFullYear();
                      }).length}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-200 text-sm mb-1">Terminées</p>
                    <p className="text-3xl font-bold">
                      {tasks.filter(t => {
                        const taskDate = parseISO(t.date);
                        return taskDate.getFullYear() === selectedDate.getFullYear() && t.status === 'termine';
                      }).length}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-200 text-sm mb-1">Taux complétion</p>
                    <p className="text-3xl font-bold">
                      {(() => {
                        const yearTasks = tasks.filter(t => {
                          const taskDate = parseISO(t.date);
                          return taskDate.getFullYear() === selectedDate.getFullYear();
                        });
                        const completed = yearTasks.filter(t => t.status === 'termine').length;
                        return yearTasks.length > 0 ? Math.round((completed / yearTasks.length) * 100) : 0;
                      })()}%
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-200 text-sm mb-1">CA total</p>
                    <p className="text-3xl font-bold">
                      {yearCA}€
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </main>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="flex-shrink-0 p-4 pb-3 border-b">
            <DialogTitle>Assigner la tâche</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedTask?.property_name}</p>
              <p className="text-sm text-gray-500">{selectedTask?.property_address}</p>
              <p className="text-sm text-gray-500">{selectedTask?.date}</p>
            </div>
            <div className="space-y-2">
              <Label>Femme(s) de ménage * <span className="text-xs text-gray-500">(max 2)</span></Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                {housekeepers.filter(h => h.is_active !== false).map(hk => {
                  const taskDate = selectedTask?.date ? new Date(selectedTask.date) : new Date();
                  const unavailability = isHousekeeperUnavailable(hk.id, taskDate);
                  const isOff = !!unavailability;
                  const isFullOff = isOff && (unavailability.period || 'full') === 'full';
                  
                  return (
                    <div 
                      key={hk.id} 
                      className={`flex items-center gap-3 p-2 rounded-lg ${
                        isFullOff 
                          ? 'bg-red-50 border border-red-200' 
                          : isOff
                            ? 'bg-orange-50 border border-orange-200'
                            : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={`hk-${hk.id}`}
                        checked={assignData.housekeeper_ids.includes(hk.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (assignData.housekeeper_ids.length < 2) {
                              const newIds = [...assignData.housekeeper_ids, hk.id];
                              setAssignData({ ...assignData, housekeeper_ids: newIds });
                              // Fetch custom prices for selected users
                              fetchCustomPrices(selectedTask?.property_id, newIds);
                            } else {
                              toast.error('Maximum 2 femmes de ménage');
                            }
                          } else {
                            const newIds = assignData.housekeeper_ids.filter(id => id !== hk.id);
                            setAssignData({ ...assignData, housekeeper_ids: newIds });
                            if (newIds.length > 0) {
                              fetchCustomPrices(selectedTask?.property_id, newIds);
                            }
                          }
                        }}
                        className="rounded border-gray-300"
                        data-testid={`checkbox-hk-${hk.id}`}
                      />
                      {hk.avatar_url ? (
                        <img 
                          src={hk.avatar_url.startsWith('http') ? hk.avatar_url : `${API_URL}/api/${hk.avatar_url}`}
                          alt={hk.name}
                          className={`w-8 h-8 rounded-full object-cover border ${
                            isFullOff ? 'border-red-300 opacity-60'
                            : isOff ? 'border-orange-300'
                            : 'border-slate-200'
                          }`}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          isFullOff ? 'bg-red-300'
                          : isOff ? 'bg-orange-300'
                          : 'bg-slate-300'
                        }`}>
                          {hk.name?.charAt(0)}
                        </div>
                      )}
                      <Label htmlFor={`hk-${hk.id}`} className={`cursor-pointer flex-1 ${
                        isFullOff ? 'text-red-600' : isOff ? 'text-orange-700' : ''
                      }`}>
                        {hk.name}
                        {hk.sector && <span className="text-xs text-gray-500 ml-2">({hk.sector})</span>}
                      </Label>
                      {isOff && (
                        <span className={`px-2 py-0.5 text-white text-xs font-bold rounded-full ${
                          (unavailability.period || 'full') === 'full' ? 'bg-red-500' : 'bg-orange-500'
                        }`}>
                          {(unavailability.period || 'full') === 'full' ? 'OFF'
                            : unavailability.period === 'morning' ? 'OFF MATIN'
                            : 'OFF APRÈS-MIDI'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {assignData.housekeeper_ids.length > 0 && (
                <p className="text-sm text-[#003366]">
                  Sélectionné(s): {housekeepers.filter(h => assignData.housekeeper_ids.includes(h.id)).map(h => h.name).join(' & ')}
                </p>
              )}
            </div>
            
            {/* Prix - Affiché seulement après sélection d'une FDM */}
            {assignData.housekeeper_ids.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Prix total (€) 
                  {assignData.housekeeper_ids.length > 1 && <span className="text-xs text-gray-500">(sera divisé entre les 2)</span>}
                </Label>
                
                {/* Affichage du tarif personnalisé détecté */}
                {assignData.priceDetails && (
                  <div className="text-sm bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                    <p className="font-medium text-emerald-700 mb-1">✓ Tarif personnalisé appliqué</p>
                    {Object.entries(assignData.priceDetails).map(([userId, price]) => {
                      const hk = housekeepers.find(h => h.id === userId);
                      return (
                        <div key={userId} className="text-emerald-600">{hk?.name || 'Inconnu'}: {price}€</div>
                      );
                    })}
                  </div>
                )}
                
                <Input 
                  type="number"
                  value={assignData.price}
                  onChange={(e) => setAssignData({ ...assignData, price: e.target.value })}
                  placeholder="Prix calculé automatiquement"
                  className={assignData.priceDetails ? 'bg-gray-50' : ''}
                  data-testid="assign-price"
                />
                <p className="text-xs text-gray-500">
                  {assignData.priceDetails 
                    ? 'Modifiez uniquement si nécessaire (cas exceptionnel)'
                    : 'Le prix sera calculé selon les tarifs définis'
                  }
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes permanentes</Label>
              <Input 
                value={assignData.notes}
                onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                placeholder="Notes générales..."
                data-testid="assign-notes"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-orange-500" />
                <span className="text-orange-700 font-semibold">Instructions spéciales pour cette prestation</span>
              </Label>
              <textarea 
                value={assignData.temporary_note || ''}
                onChange={(e) => setAssignData({ ...assignData, temporary_note: e.target.value })}
                placeholder="Ex: Déposer le linge à la laverie, Laver les vitres, Clés chez le voisin au 3ème étage..."
                className="w-full p-3 border-2 border-orange-200 rounded-lg text-sm resize-none focus:border-orange-400 focus:ring-orange-200"
                rows={3}
                data-testid="assign-temp-note"
              />
              <p className="text-xs text-gray-500">Ce texte sera affiché en évidence à la femme de ménage</p>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="urgent"
                checked={assignData.is_urgent}
                onChange={(e) => setAssignData({ ...assignData, is_urgent: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="urgent" className="text-red-600">Marquer comme urgent</Label>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 p-4 border-t bg-white gap-2">
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="flex-1">Annuler</Button>
            <Button className="flex-1 bg-[#003366] hover:bg-[#002244]" onClick={handleAssign} disabled={actionLoading || assignData.housekeeper_ids.length === 0} data-testid="confirm-assign-btn">
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Assigner {assignData.housekeeper_ids.length > 1 ? `(${assignData.housekeeper_ids.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog - Simplified Mobile-First Design */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0">
          {/* Header */}
          <DialogHeader className="flex-shrink-0 p-4 pb-3 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nouvelle prestation
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {/* Essential Fields - Always visible */}
            <div className="p-4 space-y-4">
              {/* Property Selection - Big & Clear */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">📍 Logement</Label>
                <Popover open={propertySearchOpen} onOpenChange={setPropertySearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={propertySearchOpen}
                      className={`w-full justify-between h-12 text-left font-normal ${
                        createData.property_id ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'
                      }`}
                      data-testid="select-property"
                    >
                      {createData.property_id ? (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                          <span className="truncate font-medium">
                            {properties.find(p => p.id === createData.property_id)?.name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400">Sélectionner le logement...</span>
                      )}
                      <Search className="h-4 w-4 text-slate-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[calc(100vw-2rem)] sm:w-[350px] p-0" align="start">
                    <div className="p-3 border-b bg-slate-50">
                      <Input
                        placeholder="🔍 Rechercher..."
                        value={propertySearch}
                        onChange={(e) => setPropertySearch(e.target.value)}
                        className="h-10"
                        autoFocus
                        data-testid="property-search-input"
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {filteredProperties.length === 0 ? (
                        <div className="p-6 text-center text-slate-400">
                          Aucun logement trouvé
                        </div>
                      ) : (
                        filteredProperties.map(p => (
                          <button
                            key={p.id}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-50 flex items-center gap-3 border-b last:border-0 transition-colors ${
                              createData.property_id === p.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            }`}
                            onClick={() => {
                              setCreateData({ 
                                ...createData, 
                                property_id: p.id,
                                price: '' // Prix vide - sera calculé selon la FDM sélectionnée
                              });
                              // Si des FDM sont déjà sélectionnées, recalculer le prix
                              if (createData.housekeeper_ids.length > 0) {
                                fetchCustomPricesForCreate(p.id, createData.housekeeper_ids);
                              }
                              setPropertySearchOpen(false);
                              setPropertySearch('');
                            }}
                            data-testid={`property-option-${p.id}`}
                          >
                            <div 
                              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: p.color || '#3B82F6' }}
                            >
                              {p.name?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 truncate">{p.name}</p>
                              <p className="text-xs text-slate-400 truncate">{p.address || 'Pas d\'adresse'}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date - Big & Clear */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">📅 Date</Label>
                <Input 
                  type="date"
                  value={createData.date}
                  onChange={(e) => {
                    setCreateData({ ...createData, date: e.target.value });
                    // Fetch unavailabilities for the new date
                    if (e.target.value) {
                      fetchUnavailabilitiesForDate(e.target.value);
                    }
                  }}
                  className="h-12 text-center font-medium"
                  data-testid="create-date"
                />
              </div>

              {/* Quick Assign - All Housekeepers */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">👤 Assigner à ({housekeepers.filter(h => h.is_active !== false).length} personnes)</Label>
                <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1 scrollbar-thin">
                  {housekeepers.filter(h => h.is_active !== false).map(hk => {
                    const taskDate = createData.date ? new Date(createData.date) : new Date();
                    const unavailability = isHousekeeperUnavailable(hk.id, taskDate);
                    const isOff = !!unavailability;
                    
                    return (
                      <button
                        key={hk.id}
                        onClick={async () => {
                          let newHkIds;
                          if (createData.housekeeper_ids.includes(hk.id)) {
                            newHkIds = createData.housekeeper_ids.filter(id => id !== hk.id);
                            setCreateData({ 
                              ...createData, 
                              housekeeper_ids: newHkIds,
                              price: newHkIds.length === 0 ? '' : createData.price
                            });
                          } else if (createData.housekeeper_ids.length < 2) {
                            newHkIds = [...createData.housekeeper_ids, hk.id];
                            setCreateData({ 
                              ...createData, 
                              housekeeper_ids: newHkIds
                            });
                          } else {
                            toast.error('Maximum 2 personnes');
                            return;
                          }
                          // Récupérer le tarif personnalisé si un logement est sélectionné
                          if (createData.property_id && newHkIds.length > 0) {
                            fetchCustomPricesForCreate(createData.property_id, newHkIds);
                          }
                        }}
                        className={`p-3 rounded-xl border-2 transition-all text-left relative ${
                          createData.housekeeper_ids.includes(hk.id)
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : isOff
                              ? 'border-red-300 bg-red-50'
                              : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`create-checkbox-hk-${hk.id}`}
                      >
                        {isOff && (
                          <span className={`absolute -top-1.5 -right-1.5 px-1.5 py-0.5 text-white text-[10px] font-bold rounded-full ${
                            (unavailability.period || 'full') === 'full' ? 'bg-red-500' : 'bg-orange-500'
                          }`}>
                            {(unavailability.period || 'full') === 'full' ? 'OFF'
                              : unavailability.period === 'morning' ? 'AM'
                              : 'PM'}
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          {hk.avatar_url && hk.avatar_url.length > 0 ? (
                            <img 
                              src={hk.avatar_url.startsWith('http') ? hk.avatar_url : `${API_URL}/api/${hk.avatar_url}`}
                              alt={hk.name}
                              className={`w-10 h-10 rounded-full object-cover border-2 ${
                                createData.housekeeper_ids.includes(hk.id) 
                                  ? 'border-blue-500' 
                                  : isOff 
                                    ? 'border-red-300 opacity-60' 
                                    : 'border-slate-200'
                              }`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-10 h-10 rounded-full items-center justify-center text-white font-bold text-sm ${
                              createData.housekeeper_ids.includes(hk.id) 
                                ? 'bg-blue-500' 
                                : isOff 
                                  ? 'bg-red-300' 
                                  : 'bg-slate-300'
                            }`}
                            style={{ display: hk.avatar_url && hk.avatar_url.length > 0 ? 'none' : 'flex' }}
                          >
                            {hk.name?.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isOff ? 'text-red-600' : ''}`}>{hk.name?.split(' ')[0]}</p>
                            {hk.sector && <p className="text-[10px] text-slate-400 truncate">{hk.sector}</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {createData.housekeeper_ids.length === 0 && (
                  <p className="text-xs text-slate-400 text-center">Optionnel - Vous pourrez assigner plus tard</p>
                )}
              </div>
            </div>

            {/* Advanced Options - Collapsible */}
            <details className="group border-t">
              <summary className="p-4 cursor-pointer flex items-center justify-between hover:bg-slate-50 transition-colors">
                <span className="text-sm font-semibold text-slate-600 flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Options avancées
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-4 pb-4 space-y-4 bg-slate-50">
                {/* Non Billable Option */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <Checkbox
                    id="is_not_billable"
                    checked={createData.is_not_billable}
                    onCheckedChange={(checked) => setCreateData({ ...createData, is_not_billable: checked, price: checked ? '0' : createData.price })}
                  />
                  <label htmlFor="is_not_billable" className="text-sm cursor-pointer flex-1">
                    <span className="font-medium text-amber-800">Non facturable</span>
                    <span className="block text-xs text-amber-600">Dépôt de linge, passage rapide, etc.</span>
                  </label>
                </div>

                {/* Price & Guests */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Prix €</Label>
                    <Input 
                      type="number"
                      value={createData.is_not_billable ? '0' : createData.price}
                      onChange={(e) => setCreateData({ ...createData, price: e.target.value })}
                      placeholder="50"
                      className="h-10"
                      disabled={createData.is_not_billable}
                      data-testid="create-price"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Voyageurs</Label>
                    <Input 
                      type="number"
                      value={createData.guests_count}
                      onChange={(e) => setCreateData({ ...createData, guests_count: e.target.value })}
                      placeholder="2"
                      className="h-10"
                      data-testid="create-guests"
                    />
                  </div>
                </div>

                {/* Early Start */}
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl border">
                  <Checkbox
                    id="can_start_early"
                    checked={createData.can_start_early}
                    onCheckedChange={(checked) => setCreateData({ ...createData, can_start_early: checked })}
                  />
                  <label htmlFor="can_start_early" className="text-sm cursor-pointer flex-1">
                    Peut être fait tôt
                  </label>
                </div>

                {/* Departure Time */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Heure départ voyageurs</Label>
                  <Input 
                    value={createData.guest_departure_time}
                    onChange={(e) => setCreateData({ ...createData, guest_departure_time: e.target.value })}
                    placeholder="Ex: 11h00"
                    className="h-10"
                  />
                </div>

                {/* Owner Arrival */}
                <div className="space-y-1">
                  <Label className="text-xs text-red-500 font-medium">⚠️ Arrivée propriétaire</Label>
                  <Input 
                    value={createData.owner_arrival}
                    onChange={(e) => setCreateData({ ...createData, owner_arrival: e.target.value })}
                    placeholder="Ex: RDV proprio 14h"
                    className="h-10 border-red-200"
                  />
                </div>
              </div>
            </details>
          </div>

          {/* Footer - Fixed */}
          <DialogFooter className="flex-shrink-0 p-4 border-t bg-white gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCreateDialog(false)}
              className="flex-1 h-12"
            >
              Annuler
            </Button>
            <Button 
              className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" 
              onClick={handleCreate} 
              disabled={actionLoading || !createData.property_id}
              data-testid="confirm-create-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Créer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unavailability Dialog */}
      <Dialog open={showUnavailDialog} onOpenChange={setShowUnavailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5 text-red-500" />
              Ajouter un jour OFF
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employé *</Label>
              <Select value={unavailData.user_id} onValueChange={(v) => setUnavailData({ ...unavailData, user_id: v })}>
                <SelectTrigger data-testid="select-employee-off">
                  <SelectValue placeholder="Sélectionner un employé..." />
                </SelectTrigger>
                <SelectContent>
                  {housekeepers.filter(h => h.is_active !== false).map(hk => (
                    <SelectItem key={hk.id} value={hk.id}>{hk.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input 
                type="date"
                value={unavailData.date}
                onChange={(e) => setUnavailData({ ...unavailData, date: e.target.value })}
                data-testid="off-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Période *</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setUnavailData({ ...unavailData, period: 'full' })}
                  className={`py-2.5 px-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    unavailData.period === 'full'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid="admin-period-full-btn"
                >
                  ● Journée
                </button>
                <button
                  type="button"
                  onClick={() => setUnavailData({ ...unavailData, period: 'morning' })}
                  className={`py-2.5 px-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    unavailData.period === 'morning'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid="admin-period-morning-btn"
                >
                  ◐ Matin
                </button>
                <button
                  type="button"
                  onClick={() => setUnavailData({ ...unavailData, period: 'afternoon' })}
                  className={`py-2.5 px-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    unavailData.period === 'afternoon'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid="admin-period-afternoon-btn"
                >
                  ◑ Après-midi
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Raison (optionnel)</Label>
              <Input 
                value={unavailData.reason}
                onChange={(e) => setUnavailData({ ...unavailData, reason: e.target.value })}
                placeholder="Ex: Congé, RDV médical, Vacances..."
                data-testid="off-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnavailDialog(false)}>Annuler</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={handleAddUnavailability} 
              disabled={actionLoading || !unavailData.user_id || !unavailData.date}
              data-testid="confirm-off-btn"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-500" />
              Changer la date de prestation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="font-medium text-blue-900">{selectedTask?.property_name}</p>
              <p className="text-sm text-blue-700">
                Date actuelle : {selectedTask?.date ? format(new Date(selectedTask.date), 'EEEE d MMMM yyyy', { locale: fr }) : ''}
              </p>
              {selectedTask?.housekeeper_names?.length > 0 && (
                <p className="text-sm text-blue-600 mt-1">
                  Assignée à : {selectedTask.housekeeper_names.join(' & ')}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Nouvelle date *</Label>
              <Input 
                type="date"
                value={rescheduleData.new_date}
                onChange={(e) => setRescheduleData({ ...rescheduleData, new_date: e.target.value })}
                min={format(new Date(), 'yyyy-MM-dd')}
                data-testid="reschedule-date"
              />
            </div>
            {rescheduleData.new_date && (
              <p className="text-sm text-gray-600">
                La prestation sera reportée au <strong>{format(new Date(rescheduleData.new_date), 'EEEE d MMMM yyyy', { locale: fr })}</strong>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRescheduleDialog(false)}>Annuler</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleReschedule} 
              disabled={actionLoading || !rescheduleData.new_date}
              data-testid="confirm-reschedule-btn"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirmer le changement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Task Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Supprimer la prestation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="font-medium text-red-900">{selectedTask?.property_name}</p>
              <p className="text-sm text-red-700">
                Date : {selectedTask?.date ? format(new Date(selectedTask.date), 'EEEE d MMMM yyyy', { locale: fr }) : ''}
              </p>
              {selectedTask?.housekeeper_names?.length > 0 && (
                <p className="text-sm text-red-600 mt-1">
                  Assignée à : {selectedTask.housekeeper_names.join(' & ')}
                </p>
              )}
            </div>
            <p className="text-sm text-gray-600">
              ⚠️ Cette action est irréversible. La prestation et tous ses paiements associés seront supprimés définitivement.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annuler</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={handleDeleteTask} 
              disabled={actionLoading}
              data-testid="confirm-delete-btn"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Options Dialog */}
      <Dialog open={showTaskOptionsDialog} onOpenChange={setShowTaskOptionsDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-amber-600" />
              Options de la prestation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Info prestation */}
            {selectedTaskForOptions && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="font-medium text-slate-800">{selectedTaskForOptions.property_name}</p>
                <p className="text-sm text-slate-500">{selectedTaskForOptions.date ? format(new Date(selectedTaskForOptions.date), 'EEEE d MMMM yyyy', { locale: fr }) : ''}</p>
              </div>
            )}
            
            <div className="p-3 bg-amber-50 rounded-lg space-y-4 border border-amber-200">
              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Options de planning</p>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  id="options_can_start_early"
                  checked={taskOptionsData.can_start_early}
                  onCheckedChange={(checked) => setTaskOptionsData({ ...taskOptionsData, can_start_early: checked })}
                />
                <label htmlFor="options_can_start_early" className="text-sm cursor-pointer font-medium">
                  ✅ Peut être fait tôt
                </label>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="options_is_urgent"
                  checked={taskOptionsData.is_urgent}
                  onCheckedChange={(checked) => setTaskOptionsData({ ...taskOptionsData, is_urgent: checked })}
                />
                <label htmlFor="options_is_urgent" className="text-sm cursor-pointer font-medium text-red-600">
                  🚨 Mission urgente
                </label>
              </div>

              <div className="space-y-1">
                <Label className="text-sm font-medium">🚪 Heure de départ voyageurs</Label>
                <Input 
                  value={taskOptionsData.guest_departure_time}
                  onChange={(e) => setTaskOptionsData({ ...taskOptionsData, guest_departure_time: e.target.value })}
                  placeholder="Ex: 11h, Départ à 10h..."
                />
              </div>
            </div>
            
            {/* Linge et Note */}
            <div className="p-3 bg-blue-50 rounded-lg space-y-4 border border-blue-200">
              <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Linge & Notes</p>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  id="options_linen"
                  checked={taskOptionsData.linen_included}
                  onCheckedChange={(checked) => setTaskOptionsData({ ...taskOptionsData, linen_included: checked })}
                />
                <label htmlFor="options_linen" className="text-sm cursor-pointer font-medium flex items-center gap-2">
                  <BedDouble className="h-4 w-4 text-blue-600" />
                  Linge de lit inclus
                </label>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm font-medium">📝 Note pour cette prestation</Label>
                <Input 
                  value={taskOptionsData.temporary_note}
                  onChange={(e) => setTaskOptionsData({ ...taskOptionsData, temporary_note: e.target.value })}
                  placeholder="Ex: Clé sous le paillasson, attention au chat..."
                />
                <p className="text-xs text-gray-500">Cette note sera visible par la femme de ménage</p>
              </div>
            </div>
            
            {/* Arrivée propriétaire */}
            <div className="p-3 bg-red-50 rounded-lg space-y-2 border border-red-200">
              <p className="text-xs font-semibold text-red-800 uppercase tracking-wide">⚠️ Alerte propriétaire</p>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-red-700">Arrivée propriétaire sur place</Label>
                <Input 
                  value={taskOptionsData.owner_arrival}
                  onChange={(e) => setTaskOptionsData({ ...taskOptionsData, owner_arrival: e.target.value })}
                  placeholder="Ex: Propriétaire arrive à 14h..."
                  className="border-red-200 bg-white"
                />
                <p className="text-xs text-red-600">Ce message sera affiché en gros sur la fiche</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskOptionsDialog(false)}>Annuler</Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700" 
              onClick={handleSaveTaskOptions} 
              disabled={actionLoading}
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Planning;
